import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { ClockCounterClockwiseIcon, ArrowSquareOutIcon, CaretDownIcon, CaretRightIcon, InfoIcon } from "@phosphor-icons/react";

const MESES = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];
const fmtN = (v) => Number(v || 0).toLocaleString("es-HN", { maximumFractionDigits: 2 });
const fmtF = (f) => (f ? f.split("-").reverse().join("/") : "—");

/**
 * Calendario → HISTORIAL DE VENTAS: lo vendido en ESTE MISMO MES de años anteriores (referencia para planificar
 * visitas y seguimiento). No crea citas ni representa compras futuras confirmadas.
 */
export default function CalendarSalesHistory({ year, month }) {
  const navigate = useNavigate();
  const [ops, setOps] = useState(null);
  const [f, setF] = useState({ region: "", cliente: "", producto: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [abierto, setAbierto] = useState({});
  const mes = month + 1;

  useEffect(() => { api.get("/reports/historial-ventas/opciones").then((r) => setOps(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ mes: String(mes), anio: String(year) });
    Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
    api.get(`/reports/historial-ventas/calendario?${p}`).then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [mes, year, f]);

  const abrirReportes = (extra = {}) => {
    const p = new URLSearchParams({ tab: "historial", vista: "detalle", mes: String(mes) });
    Object.entries({ ...f, ...extra }).forEach(([k, v]) => v && p.set(k, v));
    navigate(`/reportes?${p}`);
  };
  const sel = "bg-app border border-border rounded-md px-2 py-1.5 text-xs w-full";
  return (
    <div className="p-4 space-y-3" data-testid="cal-historial">
      <div className="flex items-start gap-2 text-[11px] text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-md p-2.5" data-testid="cal-historial-aviso">
        <InfoIcon size={14} className="mt-0.5 shrink-0" />
        <span><strong>REFERENCIA HISTÓRICA — NO SON ACTIVIDADES PROGRAMADAS.</strong> Ventas realizadas en {MESES[month]} de años anteriores
          para planificar visitas y seguimiento comercial. No son citas ni compras futuras confirmadas.</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <select value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} className={sel} data-testid="cal-hv-region">
          <option value="">REGIÓN: TODAS</option>{(ops?.regiones || []).map((r) => <option key={r} value={r}>{r}</option>)}</select>
        <select value={f.cliente} onChange={(e) => setF({ ...f, cliente: e.target.value })} className={sel} data-testid="cal-hv-cliente">
          <option value="">CLIENTE: TODOS</option>{(ops?.clientes || []).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select>
        <select value={f.producto} onChange={(e) => setF({ ...f, producto: e.target.value })} className={sel} data-testid="cal-hv-producto">
          <option value="">PRODUCTO: TODOS</option>{(ops?.productos || []).map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select>
        <button onClick={() => abrirReportes()} data-testid="cal-hv-abrir" className="px-2 py-1.5 text-[11px] font-semibold border border-agro-green/40 text-agro-green rounded-md flex items-center justify-center gap-1">
          <ArrowSquareOutIcon size={12} /> ABRIR EN REPORTES</button>
      </div>
      {loading && !data ? <div className="p-8 text-center text-sm text-text-muted">CARGANDO…</div>
        : !data?.anios.length ? (
          <div className="p-10 text-center text-sm text-text-muted" data-testid="cal-hv-vacio">
            <ClockCounterClockwiseIcon size={28} className="mx-auto mb-2 opacity-40" />
            NO HAY VENTAS EN {MESES[month]} DE AÑOS ANTERIORES CON ESTOS FILTROS
          </div>
        ) : (
          <div className="space-y-3 max-h-[620px] overflow-y-auto pr-1">
            {data.anios.map((a) => (
              <div key={a.anio} className="border border-border/60 rounded-md" data-testid={`cal-hv-anio-${a.anio}`}>
                <div className="px-3 py-2 bg-sidebar flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] tracking-wider">
                  <span className="font-heading text-sm text-text-primary">{MESES[month]} {a.anio}</span>
                  <span className="text-text-muted">CLIENTES: <strong className="text-text-primary">{a.clientes}</strong></span>
                  <span className="text-text-muted">OPERACIONES: <strong className="text-text-primary">{a.operaciones}</strong></span>
                  <span className="text-text-muted">UNIDADES: <strong className="text-text-primary">{a.unidades_txt || "—"}</strong></span>
                  <span className="text-text-muted ml-auto">VALOR FACTURADO: <strong className="text-agro-green">{fmtL(a.valor)}</strong></span>
                </div>
                {a.clientes_lista.map((c) => {
                  const k = `${a.anio}-${c.cliente_id}`;
                  return (
                    <div key={k} className="border-t border-border/40">
                      <button onClick={() => setAbierto({ ...abierto, [k]: !abierto[k] })} data-testid={`cal-hv-cliente-${k}`}
                        className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-white/[0.02]">
                        {abierto[k] ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />}
                        <span className="text-sm flex-1">{c.cliente}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-border text-text-secondary">
                          {c.region}{c.region_por_cliente ? " · UBICACIÓN ACTUAL" : ""}</span>
                        <span className="text-xs text-text-muted">{c.operaciones.length} LÍNEAS</span>
                        <span className="text-sm text-agro-green w-28 text-right">{fmtL(c.valor)}</span>
                      </button>
                      {abierto[k] && (
                        <table className="w-full text-xs mb-2">
                          <thead><tr className="text-[9px] tracking-wider text-text-muted text-left">
                            <th className="px-3 py-1">FECHA</th><th className="py-1">FACTURA</th><th className="py-1">PRODUCTO</th>
                            <th className="py-1 text-right">CANTIDAD</th><th className="py-1 text-right">PRECIO</th><th className="py-1 pr-3 text-right">VALOR FACTURADO</th></tr></thead>
                          <tbody>{c.operaciones.map((o, i) => (
                            <tr key={i} className="border-t border-border/20">
                              <td className="px-3 py-1">{fmtF(o.fecha)}</td><td className="py-1 font-mono">{o.numero}</td><td className="py-1">{o.producto}</td>
                              <td className="py-1 text-right">{o.cantidad ? `${fmtN(o.cantidad)} ${o.unidad}` : "—"}</td>
                              <td className="py-1 text-right">{o.precio_unitario ? fmtL(o.precio_unitario) : "—"}</td>
                              <td className="py-1 pr-3 text-right">{fmtL(o.valor)}</td></tr>))}</tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
